import React, { useState, useEffect, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/UI/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/UI/select';
import { Button } from '@/components/UI/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/UI/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/UI/radio-group';
import { Label } from '@/components/UI/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/UI/alert';
import { FileUp, Download, Loader2, X, AlertCircle, FileText, Clock, Eye } from 'lucide-react';
import { useToast } from '@/components/UI/use-toast';
import { loamsApi } from '@/services/api';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import ApprovalDialog from '@/components/EngineerDashboard/ApprovalDialog';
import CompletionCertificate from '@/components/EngineerDashboard/CompletionCertificate';
import ReportSection from '@/components/EngineerDashboard/ReportSection';
import certificateService from '@/services/certificateService';
import './Reportsection.css'; // Import the CSS file from the same directory
import Help from '@/components/EngineerDashboard/Help';
// Define Tesseract worker options - needed for the OCR functionality
const tessOptions = {
  logger: m => console.log(m),
  workerPath: 'https://unpkg.com/tesseract.js@v2.1.0/dist/worker.min.js',
  langPath: 'https://tessdata.projectnaptha.com/4.0.0',
  corePath: 'https://unpkg.com/tesseract.js-core@v2.1.0/tesseract-core.wasm.js'
};

// Constants
const PHOENICIAN_GOLD = '#A2732E';
const MOBILE_BREAKPOINT = 768;

const EngineerDashboard = () => {
  // Shared State
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [agreements, setAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState('');
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [activeTab, setActiveTab] = useState('upload');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [showCertificatePreview, setShowCertificatePreview] = useState(false);
  const [certificateGenerated, setCertificateGenerated] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [certificateLoading, setCertificateLoading] = useState(false);
  
  // Upload Tab State
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  // Generate Tab State
  const [selectedServiceType, setSelectedServiceType] = useState('all');
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [showGeneratedPreview, setShowGeneratedPreview] = useState(false);
  const [generatedPreviewUrl, setGeneratedPreviewUrl] = useState('');
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [serviceTypes, setServiceTypes] = useState([]);
  
  // Recently Downloaded Certificates State
  const [recentDownloads, setRecentDownloads] = useState([]);
  const [recentDownloadsLoading, setRecentDownloadsLoading] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Lifecycle Management
  useEffect(() => {
    fetchClusters();
    fetchRecentDownloads();
    setupResizeListener();
    
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (selectedCluster) {
      fetchAgreements();
    }
  }, [selectedCluster]);

  useEffect(() => {
    if (selectedCluster && selectedAgreement) {
      fetchCertificates();
    }
  }, [selectedCluster, selectedAgreement]);

  // Setup Functions
  const setupResizeListener = () => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  };

  const cleanup = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (generatedPreviewUrl) {
      URL.revokeObjectURL(generatedPreviewUrl);
    }
  };

  // Filter agreements based on service type
  useEffect(() => {
    if (agreements.length > 0) {
      let filtered = [...agreements];
      if (activeTab === 'generate' && selectedServiceType && selectedServiceType !== 'all') {
        filtered = filtered.filter(agreement => {
          if (!agreement.service_type) {
            console.warn('Agreement missing service_type:', agreement.ref_no);
            return false;
          }
          const normalizedAgreementType = normalizeServiceType(agreement.service_type);
          const normalizedSelectedType = normalizeServiceType(selectedServiceType);
          return normalizedAgreementType === normalizedSelectedType;
        });
      }
      setFilteredAgreements(filtered);
    } else {
      setFilteredAgreements([]);
    }
  }, [agreements, selectedServiceType, activeTab]);

  const normalizeServiceType = (serviceType) => {
    if (!serviceType) return '';
    const type = serviceType.toUpperCase();
    if (type.includes('SWIMMING') || type.includes('POOL')) {
      return 'SWIMMING';
    }
    if (type.includes('LANDSCAPING') || type.includes('LANDSCAPE') || type.includes('IRRIGATION')) {
      return 'LANDSCAPING';
    }
    return type;
  };

  const fetchRecentDownloads = async () => {
    try {
      setRecentDownloadsLoading(true);
      const certificates = await certificateService.getUserCertificates();
      setRecentDownloads(certificates);
    } catch (error) {
      console.error('Error fetching recent downloads:', error);
    } finally {
      setRecentDownloadsLoading(false);
    }
  };

  const fetchClusters = async () => {
    try {
      setLoading(true);
      setUploadError('');
      const response = await loamsApi.getFileClusters();
      if (response.data) {
        setClusters(response.data);
      }
    } catch (err) {
      setUploadError('Failed to fetch clusters');
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch clusters"
      });
    } finally {
      setLoading(false);
    }
  };

  const getServiceTypeBadge = (serviceType) => {
    if (!serviceType) return null;
    const type = serviceType.toUpperCase();
    if (type.includes('SWIMMING')) {
      return (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
          Swimming
        </span>
      );
    }
    if (type.includes('LANDSCAPING')) {
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
          Landscaping
        </span>
      );
    }
    return null;
  };

  const fetchAgreements = async () => {
    if (!selectedCluster) return;
    try {
      const response = await loamsApi.getFileClusterAgreements(selectedCluster);
      if (response.data) {
        const validAgreements = (response.data || []).map(agreement => {
          if (!agreement.ref_no) {
            console.warn('Found agreement with empty ref_no:', agreement);
            agreement.ref_no = `generated-${Math.random().toString(36).substring(2)}`;
          }
          if (!agreement.service_type) {
            const poNum = agreement.ref_no || '';
            if (poNum.includes('000364')) {
              agreement.service_type = '';
            } else if (selectedCluster === 'Akoya Master Community') {
              agreement.service_type = '';
            } else {
              agreement.service_type = '';
            }
          }
          return agreement;
        });
        setAgreements(validAgreements);
        if (activeTab === 'generate') {
          const types = [...new Set(validAgreements.map(a => a.service_type).filter(Boolean))];
          setServiceTypes(types);
        }
      }
    } catch (err) {
      console.error('Error fetching agreements:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch agreements"
      });
    }
  };

  const fetchCertificates = async () => {
    if (!selectedCluster || !selectedAgreement) return;
    try {
      setLoading(true);
      const response = await loamsApi.certificates.list(selectedCluster, selectedAgreement);
      if (response.data?.success) {
        setCertificates(response.data.certificates || []);
        if (selectedMonth) {
          const existingCert = response.data.certificates.find(
            cert => cert.month === selectedMonth
          );
          if (existingCert) {
            const previewUrl = await loamsApi.certificates.getPreviewUrl(existingCert.key);
            setPreviewUrl(previewUrl);
            if (existingCert.invoice) {
              toast({
                title: "Certificate Found",
                description: `Certificate exists with invoice ${existingCert.invoice.serial_number}`
              });
            }
          }
        }
      }
    } catch (err) {
      console.error('Error fetching certificates:', err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch certificates"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAgreementChange = (value) => {
    if (value === "no-agreements-available") return;
    setSelectedAgreement(value);
    setSelectedMonth('');
    const agreement = agreements.find(a => a.ref_no === value);
    if (!agreement) {
      console.warn('Could not find agreement data for:', value);
      return;
    }
    if (agreement.service_type) {
      const normalizedType = normalizeServiceType(agreement.service_type);
      setSelectedServiceType(normalizedType);
    }
    if (agreement.start_date && agreement.end_date) {
      generateMonthsList(new Date(agreement.start_date), new Date(agreement.end_date));
    } else {
      setMonths([]);
      console.warn('Selected agreement has invalid date range:', agreement);
    }
  };

  const validateServiceType = (cluster, agreementRef, selectedServiceType) => {
    if (selectedServiceType === 'all') return true;
    const agreement = agreements.find(a => a.ref_no === agreementRef);
    if (!agreement) {
      if (cluster === 'Akoya Master Community') {
        return selectedServiceType === 'SWIMMING';
      }
      return true;
    }
    if (!agreement.service_type) {
      const poNum = agreementRef || '';
      if (poNum.includes('000364')) {
        return selectedServiceType === 'SWIMMING';
      } else if (cluster === 'Akoya Master Community') {
        return selectedServiceType === 'SWIMMING';
      }
      return true;
    }
    const agreementType = agreement.service_type.toUpperCase();
    if (selectedServiceType === 'SWIMMING') {
      return agreementType.includes('SWIMMING') || agreementType.includes('POOL');
    } else if (selectedServiceType === 'LANDSCAPING') {
      return agreementType.includes('LANDSCAPING') || agreementType.includes('LANDSCAPE') || 
             agreementType.includes('IRRIGATION');
    }
    return false;
  };

  const generateMonthsList = (startDate, endDate) => {
    const monthsList = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      monthsList.push({
        value: `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`,
        label: `${currentDate.toLocaleString('default', { month: 'long' })} ${currentDate.getFullYear()}`
      });
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    setMonths(monthsList);
  };

  const handleMonthChange = async (value) => {
    setSelectedMonth(value);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (generatedPreviewUrl) {
      URL.revokeObjectURL(generatedPreviewUrl);
      setGeneratedPreviewUrl('');
      setShowGeneratedPreview(false);
    }
    if (activeTab === 'upload' && selectedCluster && selectedAgreement) {
      const existingCert = await getExistingCertificate(selectedCluster, selectedAgreement, value);
      if (existingCert) {
        const previewUrl = await loamsApi.certificates.getPreviewUrl(existingCert.key);
        setPreviewUrl(previewUrl);
        toast({
          title: "Info",
          description: "Certificate already exists for this month"
        });
      }
    }
  };

  const getExistingCertificate = async (cluster, agreement, month) => {
    try {
      const { exists, certificate } = await loamsApi.certificates.getApprovalStatus(cluster, agreement, month);
      return exists ? certificate : null;
    } catch (error) {
      console.error('Error checking existing certificate:', error);
      return null;
    }
  };

  const getS3Key = (cluster, agreement, month) => {
    return `COMPLETITION C/${cluster}/${agreement}/${month}.pdf`;
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a PDF file"
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "File size must be less than 10MB"
      });
      return;
    }
    setUploading(true);
    try {
      const validationResponse = await loamsApi.certificates.validateMonth({
        cluster: selectedCluster,
        agreement_ref: selectedAgreement,
        month: selectedMonth
      });
      if (validationResponse.data) {
        if (validationResponse.data.can_upload) {
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
          toast({
            title: "Processing",
            description: "Validating certificate content...",
            duration: 3000
          });
          const validation = await validateCertificateWithOCR(file, selectedAgreement, selectedMonth);
          if (!validation.isValid) {
            let errorMessage = Object.values(validation.errors).filter(Boolean).join('\n');
            toast({
              variant: "destructive",
              title: "Validation Failed",
              description: errorMessage
            });
            clearFileSelection();
            return;
          }
          setSelectedFile(file);
          toast({
            title: "Success",
            description: "Certificate validated successfully"
          });
        } else {
          if (validationResponse.data.needs_approval) {
            setShowApprovalDialog(true);
            clearFileSelection();
          } else if (validationResponse.data.contract_period) {
            toast({
              variant: "destructive",
              title: "Invalid Month",
              description: `Selected month is outside contract period (${validationResponse.data.contract_period.start} to ${validationResponse.data.contract_period.end})`
            });
            clearFileSelection();
          } else {
            toast({
              variant: "destructive",
              title: "Upload Not Allowed",
              description: validationResponse.data.message
            });
            clearFileSelection();
          }
        }
      }
    } catch (error) {
      console.error('File selection error:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to process certificate"
      });
      clearFileSelection();
    } finally {
      setUploading(false);
    }
  };

  const validateCertificateWithOCR = async (file, selectedPO, selectedMonth) => {
    try {
      const pdfjs = window['pdfjs-dist/build/pdf'];
      pdfjs.GlobalWorkerOptions.workerSrc = '//cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js';
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument(arrayBuffer).promise;
      const page = await pdf.getPage(1);
      const canvas = document.createElement('canvas');
      const viewport = page.getViewport({ scale: 2.0 });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext('2d');
      await page.render({
        canvasContext: context,
        viewport
      }).promise;
      const imageData = canvas.toDataURL('image/png');
      const worker = await Tesseract.createWorker();
      try {
        await worker.load();
        await worker.loadLanguage('eng');
        await worker.initialize('eng');
        const { data: { text } } = await worker.recognize(imageData);
        const standardizePONumber = (poNumber) => {
          return poNumber.replace(/[^0-9]/g, '');
        };
        const cleanSelectedPO = standardizePONumber(selectedPO);
        const searchForPO = (text) => {
          const poPatterns = [
            new RegExp(`PO[_\\s-]?\\d{4}[_\\s-]?\\d{6}`, 'gi'),
            new RegExp(`\\b\\d{4}[_\\s-]?\\d{6}\\b`, 'g'),
            new RegExp(`\\b\\d{10}\\b`, 'g')
          ];
          let foundNumbers = [];
          poPatterns.forEach(pattern => {
            const matches = text.match(pattern) || [];
            foundNumbers = [...foundNumbers, ...matches];
          });
          if (foundNumbers.length === 0) {
            const lines = text.split('\n');
            for (const line of lines) {
              if (line.toLowerCase().includes('po') || line.toLowerCase().includes('ref')) {
                const numbers = line.match(/\d+/g) || [];
                foundNumbers.push(...numbers);
              }
            }
          }
          const standardizedSelected = cleanSelectedPO;
          for (const found of foundNumbers) {
            const standardizedFound = standardizePONumber(found);
            if (standardizedFound === standardizedSelected || 
                standardizedFound.includes(standardizedSelected) ||
                standardizedSelected.includes(standardizedFound)) {
              return true;
            }
          }
          return false;
        };
        const poMatch = searchForPO(text);
        const [yearStr, monthStr] = selectedMonth.split('-');
        const year = yearStr;
        const monthIndex = parseInt(monthStr, 10) - 1;
        const month = new Date(year, monthIndex).toLocaleString('default', { month: 'long' }).toUpperCase();
        const upperText = text.toUpperCase();
        const monthYearPatterns = [
          `${month}\\s*${year}`,
          `${year}\\s*${month}`,
          `${month}-${year}`,
          `${year}-${month}`,
          `${month.substring(0,3)}\\s*${year}`,
          `${year}\\s*${month.substring(0,3)}`
        ];
        const monthYearRegex = new RegExp(monthYearPatterns.join('|'), 'i');
        const monthMatch = monthYearRegex.test(upperText);
        return {
          isValid: poMatch && monthMatch,
          errors: {
            po: !poMatch ? 'PO number in certificate does not match selected agreement' : null,
            month: !monthMatch ? 'Month/Year in certificate does not match selected month' : null
          }
        };
      } finally {
        await worker.terminate();
      }
    } catch (error) {
      console.error('OCR validation error:', error);
      return {
        isValid: false,
        errors: {
          general: 'Failed to validate certificate content: ' + error.message
        }
      };
      
    }
  };

  const handleApprovalRequest = async (reason) => {
    setApprovalLoading(true);
    try {
      await loamsApi.certificates.requestApproval({
        cluster: selectedCluster,
        agreement_ref: selectedAgreement,
        month: selectedMonth,
        reason
      });
      toast({
        title: "Success",
        description: "Approval request sent successfully"
      });
      setShowApprovalDialog(false);
    } catch (error) {
      console.error(error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to send approval request: " + error.message
      });
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedCluster || !selectedAgreement || !selectedMonth) {
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('cluster', selectedCluster);
      formData.append('agreement_ref', selectedAgreement);
      formData.append('month', selectedMonth);
      const response = await loamsApi.uploadWithInvoiceCreation(formData);
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Certificate uploaded successfully"
        });
        if (response.data.invoice?.exists) {
          toast({
            title: "Info",
            description: `Invoice ${response.data.invoice.serial_number} already exists for this certificate`
          });
        } else if (response.data.invoice) {
          toast({
            title: "Success",
            description: `New invoice ${response.data.invoice.serial_number} created with CC ${response.data.invoice.cc}`
          });
        }
        await fetchCertificates();
        clearFileSelection();
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: err.message || "Failed to upload certificate"
      });
    } finally {
      setUploading(false);
    }
  };

  const clearFileSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreview = async (certificateKey) => {
    try {
      const previewUrl = await loamsApi.certificates.getPreviewUrl(certificateKey);
      setPreviewUrl(previewUrl);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load preview"
      });
    }
  };

  const handleDownload = async (fileId) => {
    try {
      await loamsApi.files.download(fileId);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: "Failed to download certificate"
      });
    }
  };

  const generateSingleCertificate = async () => {
    if (!selectedMonth || !selectedAgreement) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please select a month and agreement"
      });
      return;
    }
    const agreement = agreements.find(a => a.ref_no === selectedAgreement);
    if (!agreement) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not find agreement details"
      });
      return;
    }
    if (!validateServiceType(selectedCluster, selectedAgreement, selectedServiceType)) {
      toast({
        variant: "destructive",
        title: "Service Type Mismatch",
        description: `Selected service type does not match the agreement's service type: ${agreement.service_type}`
      });
      return;
    }
    let certificateServiceType = agreement.service_type;
    if (!certificateServiceType) {
      const normalizedType = normalizeServiceType(selectedServiceType);
      certificateServiceType = normalizedType === 'SWIMMING' 
        ? 'Swimming Pool Cleaning & Maintenance' 
        : 'Landscaping & Irrigation System Maintenance';
    }
    setShowCertificatePreview(true);
    setCertificateGenerated(false);
    setCertificateUrl('');
  };

  const handleCertificateGenerated = (data) => {
    if (data.success && data.url) {
      setCertificateUrl(data.url);
      setCertificateGenerated(true);
      setGenerateSuccess(true);
      toast({
        title: "Success",
        description: "Certificate generated successfully",
        duration: 3000
      });
      updateRecentDownloads(data);
    }
  };

  const updateRecentDownloads = (certificateData) => {
    const newDownload = {
      filename: certificateData.filename,
      url: certificateData.url,
      cluster: selectedCluster,
      agreementRef: selectedAgreement,
      month: selectedMonth,
      dateDownloaded: new Date().toISOString(),
      serviceType: selectedServiceType
    };
    setRecentDownloads(prevDownloads => [newDownload, ...prevDownloads.slice(0, 9)]);
    try {
      const storedDownloads = JSON.parse(localStorage.getItem('downloaded-certificates') || '[]');
      localStorage.setItem(
        'downloaded-certificates', 
        JSON.stringify([newDownload, ...storedDownloads.slice(0, 9)])
      );
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      console.error('Error saving download to local storage:', error);
    }
  };

  const generateBatchCertificates = async () => {
    if (!selectedMonth) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please select a month for batch generation"
      });
      return;
    }
    try {
      setCertificateLoading(true);
      const response = await certificateService.generateBatchCertificates({
        month: selectedMonth,
        serviceType: selectedServiceType,
        cluster: selectedCluster,
        downloadZip: true
      });
      if (response.success) {
        toast({
          title: "Success",
          description: `${response.count} certificates generated and downloaded`,
          duration: 5000
        });
      } else {
        throw new Error(response.error || 'Failed to generate batch certificates');
      }
    } catch (error) {
      console.error('Batch generation error:', error);
      toast({
        variant: "destructive",
        title: "Batch Generation Failed",
        description: error.message || "Failed to generate certificates"
      });
    } finally {
      setCertificateLoading(false);
    }
  };

  const handleCertificateDownload = async (url, filename) => {
    try {
      setCertificateLoading(true);
      await certificateService.downloadCertificate(url, filename);
      toast({
        title: "Success",
        description: "Certificate downloaded successfully"
      });
    } catch (error) {
      console.error("Download error:", error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download certificate"
      });
    } finally {
      setCertificateLoading(false);
    }
  };

  const viewRecentCertificate = (url) => {
    if (!url) return;
    window.open(url, '_blank');
  };

  const downloadRecentCertificate = async (certificate) => {
    try {
      await certificateService.downloadCertificate(certificate.url, certificate.filename);
      toast({
        title: "Success",
        description: "Certificate downloaded successfully"
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download certificate"
      });
    }
  };

  // Loading State
  if (loading && clusters.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  // Error State
  if (uploadError && clusters.length === 0) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src={phoenicianLogo} alt="Phoenician Logo" className="h-12 w-auto" />
            <h1 className="text-lg md:text-2xl font-semibold text-slate-900">
              LOAMS Engineer Dashboard
            </h1>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex">
        {/* Sidebar for Tabs */}
        <div className="w-64 bg-white shadow-md border-r">
          <Tabs 
            defaultValue="upload" 
            onValueChange={setActiveTab} 
            className="flex flex-col h-full"
            orientation="vertical"
          >
            <TabsList className="flex flex-col space-y-2 p-4 bg-transparent">
              <TabsTrigger 
                value="upload" 
                className="w-full text-left py-2 px-4 rounded-md data-[state=active]:bg-gold data-[state=active]:text-white"
              >
                Upload Certificate
              </TabsTrigger>
              <TabsTrigger 
                value="generate" 
                className="w-full text-left py-2 px-4 rounded-md data-[state=active]:bg-gold data-[state=active]:text-white"
              >
                Generate Certificate
              </TabsTrigger>
              <TabsTrigger 
                value="report" 
                className="w-full text-left py-2 px-4 rounded-md data-[state=active]:bg-gold data-[state=active]:text-white"
              >
                Reports
              </TabsTrigger>
              <TabsTrigger 
                value="help" 
                className="w-full text-left py-2 px-4 rounded-md data-[state=active]:bg-gold data-[state=active]:text-white"
              >
                Help
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          <Tabs value={activeTab} className="w-full">
            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left Panel - Controls */}
                <div className="w-full md:w-1/2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Upload Certificate</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Cluster</Label>
                        <Select value={selectedCluster} onValueChange={setSelectedCluster}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Cluster" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            <SelectGroup>
                              {clusters.map((cluster) => (
                                <SelectItem key={cluster} value={cluster}>
                                  {cluster}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedCluster && (
                        <div className="space-y-2">
                          <Label>Agreement</Label>
                          <Select value={selectedAgreement} onValueChange={handleAgreementChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Agreement" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              <SelectGroup>
                                {filteredAgreements.length > 0 ? (
                                  filteredAgreements.map((agreement) => (
                                    <SelectItem key={agreement.ref_no} value={agreement.ref_no}>
                                      {agreement.ref_no} - {agreement.service_type}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no-agreements-available">
                                    No agreements available
                                  </SelectItem>
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedAgreement && (
                        <div className="space-y-2">
                          <Label>Month</Label>
                          <Select value={selectedMonth} onValueChange={handleMonthChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Month" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              <SelectGroup>
                                {months.map((month) => (
                                  <SelectItem key={month.value} value={month.value}>
                                    {month.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedMonth && (
                        <div className="space-y-4">
                          <input
                            type="file"
                            ref={fileInputRef}
                            accept=".pdf"
                            className="hidden"
                            onChange={handleFileSelect}
                          />
                          <div className="flex gap-4">
                            <Button
                              className="flex-1"
                              style={{ backgroundColor: PHOENICIAN_GOLD }}
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploading}
                            >
                              {uploading ? (
                                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating...</>
                              ) : (
                                <><FileUp className="mr-2 h-4 w-4" /> Select Certificate</>
                              )}
                            </Button>
                            {selectedFile && (
                              <Button
                                variant="destructive"
                                size="icon"
                                onClick={clearFileSelection}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                          {selectedFile && (
                            <div className="p-4 bg-white rounded-lg border">
                              <p className="text-sm font-medium">Selected File:</p>
                              <p className="text-sm text-slate-600 mt-1">{selectedFile.name}</p>
                              <Button
                                className="w-full mt-4"
                                style={{ backgroundColor: PHOENICIAN_GOLD }}
                                onClick={handleUpload}
                                disabled={uploading}
                              >
                                {uploading ? (
                                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
                                ) : (
                                  <>Upload Certificate</>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  {certificates.length > 0 && (
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle>Uploaded Certificates</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4">
                          {certificates.map((cert) => (
                            <div key={cert.key} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                              <span className="text-sm text-slate-700">{cert.name}</span>
                              <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => handlePreview(cert.key)}>
                                  Preview
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => handleDownload(cert.key)}>
                                  <Download className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              
                {/* Right Panel - Preview */}
                <div className="w-full md:w-1/2">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Certificate Preview</CardTitle>
                    </CardHeader>
                    <CardContent className="h-[calc(100%-4rem)]">
                      {previewUrl ? (
                        isMobile ? (
                          <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                            <div className="text-center text-slate-600">
                              <p className="mb-2">PDF Preview not available on mobile</p>
                              <p className="text-sm text-slate-500">Tap below to open the PDF</p>
                            </div>
                            <Button
                              onClick={() => window.open(previewUrl, '_blank')}
                              className="mt-4"
                              style={{ backgroundColor: PHOENICIAN_GOLD }}
                            >
                              Open PDF
                            </Button>
                          </div>
                        ) : (
                          <iframe 
                            src={previewUrl} 
                            className="w-full h-full border-0"
                            title="PDF Preview"
                          />
                        )
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          Select a certificate to preview it here
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Generate Tab */}
            <TabsContent value="generate" className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                {/* Left Panel - Controls */}
                <div className="w-full md:w-1/2 space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Generate Certificate</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Cluster</Label>
                        <Select value={selectedCluster} onValueChange={setSelectedCluster}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select Cluster" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            <SelectGroup>
                              {clusters.map((cluster) => (
                                <SelectItem key={cluster} value={cluster}>
                                  {cluster}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedCluster && (
                        <div className="space-y-2">
                          <Label>Service Type</Label>
                          <RadioGroup 
                            value={selectedServiceType} 
                            onValueChange={setSelectedServiceType}
                            className="flex space-x-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="all" id="all" />
                              <Label htmlFor="all">All</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="LANDSCAPING" id="landscaping" />
                              <Label htmlFor="landscaping">Landscaping</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="SWIMMING" id="swimming" />
                              <Label htmlFor="swimming">Swimming Pool</Label>
                            </div>
                          </RadioGroup>
                        </div>
                      )}
                      {selectedCluster && (
                        <div className="space-y-2">
                          <Label>Agreement</Label>
                          <Select value={selectedAgreement} onValueChange={handleAgreementChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Agreement" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              <SelectGroup>
                                {agreements.length > 0 ? (
                                  agreements.map((agreement) => (
                                    <SelectItem key={agreement.ref_no} value={agreement.ref_no}>
                                      {agreement.ref_no} - {agreement.service_type}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="no-agreements-available">
                                    No agreements available
                                  </SelectItem>
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedAgreement && (
                        <div className="space-y-2">
                          <Label>Month</Label>
                          <Select value={selectedMonth} onValueChange={handleMonthChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select Month" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60 overflow-y-auto">
                              <SelectGroup>
                                {months.map((month) => (
                                  <SelectItem key={month.value} value={month.value}>
                                    {month.label}
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {selectedMonth && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <Button 
                            className="w-full" 
                            style={{ backgroundColor: PHOENICIAN_GOLD }}
                            onClick={showCertificatePreview ? undefined : generateSingleCertificate}
                            disabled={certificateLoading || !selectedMonth || !selectedAgreement}
                          >
                            {certificateLoading ? (
                              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...</>
                            ) : (
                              <><FileText className="mr-2 h-4 w-4" /> Generate Certificate</>
                            )}
                          </Button>
                          <Button 
                            className="w-full"
                            variant="outline"
                            onClick={generateBatchCertificates}
                            disabled={certificateLoading || !selectedMonth}
                          >
                            {certificateLoading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="mr-2 h-4 w-4" />
                            )}
                            Generate All Certificates
                          </Button>
                        </div>
                      )}
                      {generateSuccess && !showCertificatePreview && (
                        <Alert className="bg-green-50 border-green-200 text-green-800 mt-4">
                          <AlertTitle className="flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-4 w-4 mr-2"
                            >
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                              <polyline points="22 4 12 14.01 9 11.01"></polyline>
                            </svg>
                            Certificate Generated Successfully
                          </AlertTitle>
                          <AlertDescription>
                            Your certificate has been generated and can be downloaded or previewed on the right.
                          </AlertDescription>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                  {!showCertificatePreview && !generateSuccess && (
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle>About Certificate Generation</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-blue-700 mb-4">
                          The certificate generator allows you to create a completion certificate without the need to manually create and upload one. 
                          Generated certificates follow the standard LOAMS format and are automatically saved to the system.
                        </p>
                        <ul className="list-disc pl-5 text-sm text-blue-700 space-y-1">
                          <li>Select a cluster, service type (optional), and agreement</li>
                          <li>Choose the month for which you want to generate a certificate</li>
                          <li>Click "Generate Certificate" to create and preview the certificate</li>
                          <li>Download or save the generated certificate</li>
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              
                {/* Right Panel - Preview */}
                <div className="w-full md:w-1/2">
                  <Card className="h-full">
                    <CardHeader>
                      <CardTitle>Certificate Preview</CardTitle>
                      <CardDescription>
                        {certificateGenerated && (
                          <div className="text-sm text-green-600 mt-1">
                            Certificate generated successfully
                          </div>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="h-[calc(100%-4rem)]">
                      {showCertificatePreview ? (
                        <CompletionCertificate 
                          cluster={selectedCluster}
                          agreementRef={selectedAgreement}
                          month={selectedMonth}
                          serviceType={
                            (() => {
                              const agreement = agreements.find(a => a.ref_no === selectedAgreement);
                              if (agreement && agreement.service_type) {
                                return agreement.service_type;
                              } else if (selectedCluster === 'Akoya Master Community') {
                                return 'Swimming Pool Cleaning & Maintenance';
                              } else {
                                return selectedServiceType === 'SWIMMING' 
                                  ? 'Swimming Pool Cleaning & Maintenance' 
                                  : 'Landscaping & Irrigation System Maintenance';
                              }
                            })()
                          }
                          onGenerated={handleCertificateGenerated}
                        />
                      ) : certificateUrl ? (
                        isMobile ? (
                          <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
                            <div className="text-center text-slate-600">
                              <p className="mb-2">PDF Preview not available on mobile</p>
                              <p className="text-sm text-slate-500">Tap below to open the PDF</p>
                            </div>
                            <Button
                              onClick={() => window.open(certificateUrl, '_blank')}
                              className="mt-4"
                              style={{ backgroundColor: PHOENICIAN_GOLD }}
                            >
                              Open PDF
                            </Button>
                          </div>
                        ) : (
                          <iframe 
                            src={certificateUrl} 
                            className="w-full h-full border-0"
                            title="PDF Preview"
                          />
                        )
                      ) : (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          Generate a certificate to preview it here
                        </div>
                      )}
                      {(certificateGenerated || certificateUrl) && (
                        <div className="mt-4 flex gap-2">
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => window.open(certificateUrl, '_blank')}
                          >
                            <Eye className="h-4 w-4 mr-2" /> View
                          </Button>
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => handleCertificateDownload(certificateUrl, `Certificate_${selectedCluster}_${selectedAgreement}_${selectedMonth}.pdf`)}
                          >
                            <Download className="h-4 w-4 mr-2" /> Download
                          </Button>
                          {certificateGenerated && (
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => setShowCertificatePreview(false)}
                            >
                              Close Preview
                            </Button>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>

            {/* Report Tab */}
            <TabsContent value="report">
              <ReportSection />
            </TabsContent>

            <TabsContent value="help">
              <Help />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Approval Dialog */}
      <ApprovalDialog
        open={showApprovalDialog}
        onClose={() => setShowApprovalDialog(false)}
        onSubmit={handleApprovalRequest}
        month={selectedMonth}
        loading={approvalLoading}
      />
    </div>
  );
};

export default EngineerDashboard;